import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer, Form, Input, InputNumber, Button, Row, Col, Divider,
  Checkbox, Tag, message, Spin, Space, Typography,
} from 'antd';
import dayjs from 'dayjs';
import { supabase } from '../../api/supabase';
import { regenerateDocument, generateDocumentWithGoogleDrive } from '../../utils/googleDocumentGenerator';
import { colors } from '../../styles/monochromeTheme';

const { TextArea } = Input;
const { Text } = Typography;

// Field schemas per category
const FIELD_SCHEMAS = {
  bateaux: {
    client: [
      { name: 'client_name', label: 'Client', required: true },
      { name: 'charterer', label: 'Charterer' },
      { name: 'charter_date', label: 'Date charter' },
    ],
    details: [
      { name: 'brand_of_vessel', label: 'Marque' },
      { name: 'name_of_vessel', label: 'Nom bateau' },
      { name: 'length', label: 'Longueur' },
      { name: 'cruising_area', label: 'Zone de navigation' },
      { name: 'from_hour', label: 'Heure début' },
      { name: 'to_hour', label: 'Heure fin' },
      { name: 'pax_number', label: 'Passagers' },
      { name: 'flight_info', label: 'Vol info' },
    ],
    pricing: [
      { name: 'boat_price', label: 'Prix bateau', type: 'number' },
      { name: 'transport_price', label: 'Transport', type: 'number' },
      { name: 'transport_text', label: 'Transport (texte)' },
      { name: 'taxes_price', label: 'Taxes', type: 'number' },
      { name: 'taxes_text', label: 'Taxes (texte)' },
      { name: 'discount_price', label: 'Remise', type: 'number' },
      { name: 'discount_text', label: 'Remise (texte)' },
      { name: 'extra_hour_price', label: 'Heure sup.', type: 'number' },
      { name: 'extra_hour_text', label: 'Heure sup. (texte)' },
      { name: 'custom_price', label: 'Custom', type: 'number' },
      { name: 'custom_text', label: 'Custom (texte)' },
      { name: 'total_price', label: 'Total', type: 'number', required: true },
    ],
  },
  seabob: {
    client: [
      { name: 'client_name', label: 'Client', required: true },
      { name: 'charter_date', label: 'Date charter' },
    ],
    details: [
      { name: 'departure_hour', label: 'Heure départ' },
      { name: 'returning_hour', label: 'Heure retour' },
      { name: 'spot', label: 'Spot' },
    ],
    pricing: [
      { name: 'seabob_number', label: 'Nb Seabob' },
      { name: 'seabob_duration', label: 'Durée Seabob' },
      { name: 'seabob_price', label: 'Prix Seabob', type: 'number' },
      { name: 'esurf_number', label: 'Nb E-Surf' },
      { name: 'esurf_duration', label: 'Durée E-Surf' },
      { name: 'esurf_price', label: 'Prix E-Surf', type: 'number' },
      { name: 'additional_row', label: 'Additionnel' },
      { name: 'addtional_price', label: 'Prix additionnel', type: 'number' },
      { name: 'total_price', label: 'Total', type: 'number', required: true },
    ],
  },
  jetski: {
    client: [
      { name: 'client_name', label: 'Client', required: true },
      { name: 'charterer', label: 'Charterer' },
      { name: 'charter_date', label: 'Date charter' },
    ],
    details: [
      { name: 'spot', label: 'Spot' },
    ],
    pricing: [
      { name: 'jetski_number', label: 'Nb Jetski' },
      { name: 'jetski_duration', label: 'Durée Jetski' },
      { name: 'jetski_price', label: 'Prix Jetski', type: 'number' },
      { name: 'flyboard_duration', label: 'Durée Flyboard' },
      { name: 'flyboard_price', label: 'Prix Flyboard', type: 'number' },
      { name: 'total_price', label: 'Total', type: 'number', required: true },
    ],
  },
  lunch: {
    client: [
      { name: 'client_name', label: 'Client', required: true },
      { name: 'charter_date', label: 'Date charter' },
    ],
    details: [],
    pricing: [
      { name: 'menu_text', label: 'Menu', type: 'textarea' },
      { name: 'menu_price', label: 'Prix menu', type: 'number' },
      { name: 'additional_text', label: 'Additionnel' },
      { name: 'additional_price', label: 'Prix additionnel', type: 'number' },
      { name: 'total_price', label: 'Total', type: 'number', required: true },
    ],
  },
  custom: {
    client: [
      { name: 'client_name', label: 'Client', required: true },
      { name: 'charter_date', label: 'Date charter' },
    ],
    details: [],
    pricing: [
      { name: 'custom_text', label: 'Description', type: 'textarea' },
      { name: 'custom_price', label: 'Prix', type: 'number' },
      { name: 'total_price', label: 'Total', type: 'number', required: true },
    ],
  },
};

// Extract numeric value from formatted price strings like "1600€ (BOAT PRICE)" or "+ 200€"
const extractNumericValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return value;
  const match = String(value).match(/[\d.]+/);
  return match ? parseFloat(match[0]) : null;
};

// Build formatted document_data for bateaux prices (Contrat format)
const formatBateauxContratPrices = (values) => ({
  boat_price: values.boat_price ? `${values.boat_price}€ (BOAT PRICE)` : '',
  transport_price: values.transport_price ? `+ ${values.transport_price}€` : '',
  taxes_price: values.taxes_price ? `+ ${values.taxes_price}€` : '',
  discount_price: values.discount_price ? `- ${values.discount_price}€` : '',
  extra_hour_price: values.extra_hour_price ? `+ ${values.extra_hour_price}€` : '',
  custom_price: values.custom_price ? `+ ${values.custom_price}€` : '',
  total_price: values.total_price ? `${values.total_price} EUR` : '',
});

// Build document_data from form values for a specific doc type
const buildDocumentData = (formValues, docType, category, existingData = {}) => {
  const data = { ...existingData };

  // Copy all non-price fields directly
  const allFields = [
    ...(FIELD_SCHEMAS[category]?.client || []),
    ...(FIELD_SCHEMAS[category]?.details || []),
    ...(FIELD_SCHEMAS[category]?.pricing || []),
  ];
  for (const field of allFields) {
    if (formValues[field.name] !== undefined) {
      if (field.type === 'number') {
        // Price fields: keep raw for now, format below
        data[field.name] = formValues[field.name] != null ? String(formValues[field.name]) : '';
      } else {
        data[field.name] = formValues[field.name] || '';
      }
    }
  }

  // Handle seabob date field inconsistency
  if (category === 'seabob' && formValues.charter_date) {
    data.charterer_date = formValues.charter_date;
    data.charter_date = formValues.charter_date;
  }

  // Format prices for bateaux
  if (category === 'bateaux') {
    Object.assign(data, formatBateauxContratPrices(formValues));
  } else {
    // For other categories, just store numeric as string
    if (formValues.total_price != null) {
      data.total_price = String(formValues.total_price);
    }
  }

  // Add date fields for Confirmation/Facturation
  if (docType === 'Confirmation' || docType === 'Facturation') {
    data.today_date = dayjs().format('DD MMMM YYYY');
    data.generation_date = dayjs().format('DD MMMM YYYY');
  }

  return data;
};

export default function CharterEditDrawer({ open, onClose, charter, onSuccess }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState('');
  const [selectedDocTypes, setSelectedDocTypes] = useState([]);
  const [invoiceNumber, setInvoiceNumber] = useState('');

  const category = charter?.category || 'bateaux';
  const schema = FIELD_SCHEMAS[category] || FIELD_SCHEMAS.bateaux;
  const contrat = charter?.documents?.['Contrat'];
  const hasConfirmation = !!charter?.documents?.['Confirmation'];
  const hasFacturation = !!charter?.documents?.['Facturation'];

  // Populate form when charter changes
  useEffect(() => {
    if (!charter || !open) return;

    const docData = contrat?.document_data || {};
    const formValues = {};

    // Parse all fields
    const allFields = [...(schema.client || []), ...(schema.details || []), ...(schema.pricing || [])];
    for (const field of allFields) {
      const rawValue = docData[field.name] ?? docData[field.name === 'charter_date' && category === 'seabob' ? 'charterer_date' : ''];
      if (field.type === 'number') {
        formValues[field.name] = extractNumericValue(rawValue ?? docData[field.name]);
      } else {
        formValues[field.name] = docData[field.name] || '';
      }
    }

    form.setFieldsValue(formValues);

    // Default: check existing doc types
    const defaults = ['Contrat'];
    if (hasConfirmation) defaults.push('Confirmation');
    if (hasFacturation) defaults.push('Facturation');
    setSelectedDocTypes(defaults);
  }, [charter, open, form, schema, category, contrat, hasConfirmation, hasFacturation]);

  // Auto-generate invoice number when Facturation is checked and doesn't exist
  const generateInvoiceNumber = useCallback(async () => {
    if (hasFacturation) {
      const existingInvoice = charter.documents['Facturation']?.document_data?.invoice_number;
      setInvoiceNumber(existingInvoice || '');
      return;
    }
    const today = dayjs();
    const dateStr = today.format('DDMMYYYY');
    const { data: todayInvoices } = await supabase
      .from('documents')
      .select('id')
      .eq('type', 'Facturation')
      .gte('created_at', today.startOf('day').toISOString())
      .lte('created_at', today.endOf('day').toISOString());
    const count = (todayInvoices || []).length + 1;
    setInvoiceNumber(`${dateStr}-${count}`);
  }, [hasFacturation, charter]);

  useEffect(() => {
    if (open && selectedDocTypes.includes('Facturation')) {
      generateInvoiceNumber();
    }
  }, [open, selectedDocTypes, generateInvoiceNumber]);

  const handleSaveAndRegenerate = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      for (const docType of selectedDocTypes) {
        const existingDoc = charter.documents[docType];
        setProgress(`${docType}...`);

        // Build document_data with proper formatting
        const existingData = contrat?.document_data || {};
        const docData = buildDocumentData(values, docType, category, existingData);

        // Add invoice number for Facturation
        if (docType === 'Facturation') {
          docData.invoice_number = invoiceNumber;
          docData.charterer = values.charterer || values.client_name;
        }

        if (existingDoc) {
          // Regenerate: new Google Doc, update existing Supabase row
          const result = await regenerateDocument(docType, docData, category);
          await supabase
            .from('documents')
            .update({
              document_data: docData,
              google_doc_id: result.googleDocId,
              docx_url: result.documentUrl,
              pdf_url: result.documentUrl,
              client_name: values.client_name,
              charter_date: values.charter_date,
            })
            .eq('id', existingDoc.id);
        } else {
          // Create new: insert via full generator
          await generateDocumentWithGoogleDrive(
            docType,
            docData,
            charter.charter_id,
            category
          );
        }
      }

      message.success('Charter mis à jour et documents régénérés');
      setProgress('');
      onSuccess?.();
      onClose();
    } catch (error) {
      if (error.message?.startsWith('NOT_AUTHENTICATED:')) {
        message.error('Veuillez vous connecter à Google Drive');
      } else {
        message.error('Erreur: ' + error.message);
      }
    } finally {
      setSaving(false);
      setProgress('');
    }
  };

  const renderField = (field) => {
    if (field.type === 'textarea') {
      return (
        <Col span={24} key={field.name}>
          <Form.Item name={field.name} label={field.label} rules={field.required ? [{ required: true }] : []}>
            <TextArea rows={3} />
          </Form.Item>
        </Col>
      );
    }
    if (field.type === 'number') {
      return (
        <Col span={12} key={field.name}>
          <Form.Item name={field.name} label={field.label} rules={field.required ? [{ required: true }] : []}>
            <InputNumber style={{ width: '100%' }} min={0} suffix="€" />
          </Form.Item>
        </Col>
      );
    }
    return (
      <Col span={12} key={field.name}>
        <Form.Item name={field.name} label={field.label} rules={field.required ? [{ required: true }] : []}>
          <Input />
        </Form.Item>
      </Col>
    );
  };

  if (!charter) return null;

  const sectionCard = {
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid #ebebeb',
    padding: '20px 24px',
    marginBottom: 12,
  };

  const sectionLabel = {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#999',
    marginBottom: 16,
    paddingBottom: 10,
    borderBottom: '1px solid #f0f0f0',
  };

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: '#1a1a1a', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 15,
          }}>
            ✏️
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2 }}>
              Modifier Charter
            </div>
            <div style={{ fontSize: 12, color: '#888', fontWeight: 400, lineHeight: 1.4 }}>
              {charter.client_name || 'Client'}
            </div>
          </div>
        </div>
      }
      placement="right"
      width={740}
      open={open}
      onClose={onClose}
      styles={{
        header: {
          background: '#ffffff',
          borderBottom: '1px solid #f0f0f0',
          padding: '16px 24px',
        },
        body: {
          background: '#F5F5F7',
          padding: '16px 20px',
          paddingBottom: 80,
        },
        footer: {
          background: '#ffffff',
          borderTop: '1px solid #f0f0f0',
          padding: '14px 24px',
        },
      }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ minHeight: 24 }}>
            {saving && progress && (
              <Space>
                <Spin size="small" />
                <Text style={{ fontSize: 12, color: '#888' }}>{progress}</Text>
              </Space>
            )}
          </div>
          <Space>
            <Button
              onClick={onClose}
              style={{ borderRadius: 8, height: 38, paddingInline: 20, fontWeight: 500 }}
            >
              Annuler
            </Button>
            <Button
              type="primary"
              onClick={handleSaveAndRegenerate}
              loading={saving}
              disabled={selectedDocTypes.length === 0}
              style={{
                background: '#1a1a1a', borderColor: '#1a1a1a',
                borderRadius: 8, height: 38, paddingInline: 20, fontWeight: 600,
              }}
            >
              Enregistrer & Régénérer
            </Button>
          </Space>
        </div>
      }
    >
      <style>{`
        .charter-edit-drawer .ant-form-item-label > label {
          font-size: 11px !important;
          font-weight: 600 !important;
          color: #888 !important;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .charter-edit-drawer .ant-input,
        .charter-edit-drawer .ant-input-number,
        .charter-edit-drawer .ant-input-number-input {
          border-radius: 8px !important;
          border-color: #e8e8e8 !important;
          background: #fafafa !important;
          font-size: 13px !important;
        }
        .charter-edit-drawer .ant-input:focus,
        .charter-edit-drawer .ant-input-number:focus,
        .charter-edit-drawer .ant-input-number-focused {
          border-color: #1a1a1a !important;
          background: #fff !important;
          box-shadow: 0 0 0 2px rgba(0,0,0,0.06) !important;
        }
        .charter-edit-drawer .ant-form-item {
          margin-bottom: 14px !important;
        }
      `}</style>

      <Form form={form} layout="vertical" size="middle" className="charter-edit-drawer">

        {/* Client section */}
        <div style={sectionCard}>
          <span style={sectionLabel}>Client</span>
          <Row gutter={16}>{schema.client.map(renderField)}</Row>
        </div>

        {/* Details section */}
        {schema.details.length > 0 && (
          <div style={sectionCard}>
            <span style={sectionLabel}>Détails</span>
            <Row gutter={16}>{schema.details.map(renderField)}</Row>
          </div>
        )}

        {/* Pricing section */}
        <div style={sectionCard}>
          <span style={sectionLabel}>Tarification</span>
          <Row gutter={16}>{schema.pricing.map(renderField)}</Row>
        </div>

        {/* Documents section */}
        <div style={sectionCard}>
          <span style={sectionLabel}>Documents à régénérer</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {['Contrat', 'Confirmation', 'Facturation'].map(type => {
              const active = selectedDocTypes.includes(type);
              const exists = !!charter.documents[type];
              return (
                <div
                  key={type}
                  onClick={() => {
                    setSelectedDocTypes(prev =>
                      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
                    );
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: active ? '#1a1a1a' : '#f0f0f0',
                    border: `1.5px solid ${active ? '#1a1a1a' : '#d9d9d9'}`,
                    borderRadius: 8,
                    padding: '7px 14px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    userSelect: 'none',
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 600, color: active ? '#fff' : '#333' }}>
                    {active ? '✓ ' : ''}{type}
                  </span>
                  {!exists && (
                    <span style={{
                      fontSize: 9, padding: '1px 5px', borderRadius: 4,
                      background: active ? 'rgba(255,255,255,0.2)' : '#e8f4ff',
                      border: `1px solid ${active ? 'rgba(255,255,255,0.3)' : '#b3d9ff'}`,
                      color: active ? '#fff' : '#0066cc',
                      fontWeight: 600,
                    }}>
                      nouveau
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {selectedDocTypes.includes('Facturation') && (
            <div style={{
              marginTop: 16, padding: '12px 14px',
              background: '#f8f8f8', borderRadius: 8, border: '1px solid #ebebeb',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <Text style={{ fontSize: 12, color: '#888', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                N° Facture
              </Text>
              <Input
                value={invoiceNumber}
                onChange={e => setInvoiceNumber(e.target.value)}
                style={{ width: 180, borderRadius: 8, borderColor: '#e0e0e0', background: '#fff', fontSize: 13 }}
              />
            </div>
          )}
        </div>

      </Form>
    </Drawer>
  );
}
